import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Header, Grid, Form, Button, Loader } from 'semantic-ui-react';
import { reduxForm, Field, SubmissionError } from 'redux-form';
import { combineValidators, composeValidators, createValidator } from 'revalidate';
import TextInput from '../../app/common/Form/TextInput';
import { openModal } from '../modals/modalActions';
import API from '../../APIconfig';
import { getCappingLimitsForUser, limitUpdated } from './LimitActions';
import { generateOTP } from '../OTP/OTPactions';
import { toastr } from 'react-redux-toastr';
import transactionLimitIcon from "./transaction-limit.png";
import './transaction-limit.css';

const formFieldErrorCodeMapping = {
  509: 'dailytransactionlimit',
  510: 'dailydebitlimit',
  511: 'monthlywithdrawlimit',
  512: 'withdrawtransactionlimit'
}

const formName = 'transactionLimitForm';

// Redux actions
const actions = {
  getCappingLimitsForUser,
  limitUpdated,
  openModal,
  generateOTP
};

// Redux state
const mapState = state => ({
  auth: state.auth,
  async: state.async,
  payment: state.payment,
  otp: state.otp,
  initialValues: {
    dailydebitlimit:
      state.limit &&
      state.limit.limit &&
      state.limit.limit.response &&
      state.limit.limit.response.upperCappingLimits &&
      state.limit.limit.response.upperCappingLimits.DAILY_DEBIT_LIMIT,
    dailytransactionlimit:
      state.limit &&
      state.limit.limit &&
      state.limit.limit.response &&
      state.limit.limit.response.upperCappingLimits &&
      state.limit.limit.response.upperCappingLimits.DEBIT_TRANSACTION_LIMIT,
    monthlywithdrawlimit:
      state.limit &&
      state.limit.limit &&
      state.limit.limit.response &&
      state.limit.limit.response.upperCappingLimits &&
      state.limit.limit.response.upperCappingLimits.MONTHLY_WITHDRAW_LIMIT,
    withdrawtransactionlimit:
      state.limit &&
      state.limit.limit &&
      state.limit.limit.response &&
      state.limit.limit.response.upperCappingLimits &&
      state.limit.limit.response.upperCappingLimits.WITHDRAW_TRANSACTION_LIMIT
  }
});

const isNumber = createValidator(
  message => value => {
    if (!value) return;
    if (!/^[0-9]*$/.test(value)) {
      return message;
    }
  },

  field => `${field} should be a number without decimals`
);

const isTransactionAmountLimit = () => createValidator(
  message => value => {
    if (!value) return;
    if (value < 0 || value > 100000) {
      return `${message}`;
    }
  },

  field => `${field} limit is 1-100000`
);

const isTransactionCountLimit = () => createValidator(
  message => value => {
    if (!value) return;
    if (value < 0 || value > 1000) {
      return `${message}`;
    }
  },

  field => `${field} limit is 1-1000`
);

const isWithdrawTxnAmtLimit = () => createValidator(
  message => value => {
    if (!value) return;
    if (value < 0 || value > 5000) {
      return `${message}`;
    }
  },

  field => `${field} limit is 1-5000`
);

const validate = combineValidators({
  dailydebitlimit: composeValidators(
    isNumber({ message: "Should be a number without decimals" }),
    isTransactionCountLimit()({message: "Should be in the range of 0 - 1000"})
    )(),

    dailytransactionlimit: composeValidators(
      isNumber({ message: "Should be a number without decimals" }),
      isTransactionAmountLimit()({message: "Should be in the range of 0 - 100000"})
  )(),

  monthlywithdrawlimit: composeValidators(
    isNumber({ message: "Should be a number without decimals" }),
    isWithdrawTxnAmtLimit()({message: "Should be in the range of 0 - 5000"})
  )(),

  withdrawtransactionlimit: composeValidators(
    isNumber({ message: "Should be a number without decimals" }),
    isTransactionAmountLimit()({message: "Should be in the range of 0 - 100000"})
  )()
});

class CappingLimit extends Component {
  static propTypes = {
    auth: PropTypes.object,
    getCappingLimitsForUser: PropTypes.func.isRequired,
    limitUpdated: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    payment: PropTypes.object
  };

  // init state
  state = {};

  submit = async (values) => {
    const { auth, limitUpdated, openModal, generateOTP } = this.props;
    let _r = {};

    _r['request'] = {
      cappingLimitsInfo: [
        {
          cappingLimitTypes: 'DAILY_DEBIT_LIMIT',
          cappingLimitValue: parseInt(values.dailydebitlimit)
        },
        {
          cappingLimitTypes: 'DEBIT_TRANSACTION_LIMIT',
          cappingLimitValue: parseInt(values.dailytransactionlimit)
        },
        {
          cappingLimitTypes: 'MONTHLY_WITHDRAW_LIMIT',
          cappingLimitValue: parseInt(values.monthlywithdrawlimit)
        },
        {
          cappingLimitTypes: 'WITHDRAW_TRANSACTION_LIMIT',
          cappingLimitValue: parseInt(values.withdrawtransactionlimit)
        }
      ]
    };

    await generateOTP(API.bank.generateOTPforUpdateLimit, _r, auth.csrfRequestIdentifier, true);

    // Construct payload
    const payload = {
      values: _r,
      csrfRequestIdentifier: auth.csrfRequestIdentifier,
      forwardAction: limitUpdated,
      api: {
        generate: API.bank.generateOTPforUpdateLimit,
        verify: API.bank.verifyOTPforUpdateLimit
      },
      // generateOTPonLoad: true
    };

    if (this.props.otp.error) {
      let toastMessage = 'There was some error. Please verify your data.';
      if (this.props.otp.error.errorCode === 1003) {
        toastMessage = this.props.otp.error.errorMessage;
      }
      toastr.error(toastMessage);
      let submitError = {_error: 'Failed'};
      submitError[formFieldErrorCodeMapping[this.props.otp.error.errorCode]] = this.props.otp.error.errorMessage;
      throw new SubmissionError(submitError);
    } else {
      // Request to open OTP modal
      openModal('OTPModal', { payload });
    }
  };

  render() {
    const {  handleSubmit, submitting, payment, pristine } = this.props;
    return (
      <div id="update-capping-limit">
        <Header as="h1" style={{background: `url(${transactionLimitIcon}) no-repeat top left`}}>
          Transaction Limits {' '}
          <Loader
            active={!payment.balance}
            inline
            size="tiny"
          />
        </Header>
        <Form onSubmit={handleSubmit(this.submit)}>
          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={12}>
                <div className="transaction-info">
                  Secure your Freecharge Balance by setting up limits for different types of transactions.
                </div>
              </Grid.Column>
            </Grid.Row>
            {/* <Grid.Row /> */}
            <Grid.Row>
              <Grid.Column width={9}>
                <Header
                  as="h4"
                  content="Maximum amount per transaction"
                  subheader="Set a limit on the amount for each merchant payment transaction"
                />
              </Grid.Column>

              <Grid.Column width={4}>
                <Field
                  name="dailytransactionlimit"
                  placeholder="0"
                  type="tel"
                  component={TextInput}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={9}>
                <Header
                  as="h4"
                  content="Maximum number of transactions per day"
                  subheader="Set a limit on the number of merchant payment transactions to be done in a month"
                />
              </Grid.Column>

              <Grid.Column width={4}>
                <Field
                  name="dailydebitlimit"
                  placeholder="0"
                  type="tel"
                  component={TextInput}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={9}>
                <Header
                  as="h4"
                  content="Maximum amount per Withdraw transaction"
                  subheader="Set a limit on the amount for each Withdraw transaction"
                />
              </Grid.Column>

              <Grid.Column width={4}>
                <Field
                  name="monthlywithdrawlimit"
                  placeholder="0"
                  type="tel"
                  component={TextInput}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={9}>
                <Header
                  as="h4"
                  content="Maximum amount for Withdraw transactions per month"
                  subheader="Set a limit on the amount for Withdraw transactions to be done in a month"
                />
              </Grid.Column>

              <Grid.Column width={4}>
                <Field
                  name="withdrawtransactionlimit"
                  placeholder="0"
                  type="tel"
                  component={TextInput}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={9}>
                <div className="transaction-note">
                  NOTE: Withdraw transactions are allowed only to Full KYC Wallet users. These limits will be applicable only to Full KYC Wallet Users.
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={7}>
                <Button
                  className="fcbtn fcprimary small width100"
                  disabled={submitting || pristine}
                  type="submit"
                >
                  UPDATE LIMIT
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </div>
    );
  }

  componentDidMount() {
    const { auth: { csrfRequestIdentifier}, getCappingLimitsForUser } = this.props;

    if (typeof getCappingLimitsForUser === 'function') getCappingLimitsForUser(csrfRequestIdentifier);
  }
}

// Redux connect and reduxForm HOC on statefull component 'CappingLimit'
export default connect(
  mapState,
  actions
)(
  reduxForm({
    form: formName,
    enableReinitialize: true,
    validate
  })(CappingLimit)
);
